/**
 * Thin fetch wrapper.
 *
 * Everything is same-origin and cookie-authenticated, so there is no token to
 * juggle on the client — which also means no token sitting in localStorage for
 * a stray script to read.
 */

import { readStoredLocale, translate } from '../i18n/locale';
import type { Vars } from '../i18n/locale';

export class ApiError extends Error {
  readonly status: number;
  readonly field?: string;
  /** The server's stable error code, when it sent one. */
  readonly code?: string;

  constructor(status: number, message: string, field?: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.field = field;
    this.code = code;
  }
}

/**
 * Render a server error in the user's chosen language.
 *
 * The server sends a stable `code` plus the `params` that fill it, and the
 * matching text lives in the same dictionary the rest of the UI uses — so a
 * thrown error follows the ID|EN switch exactly, and every call site can keep
 * reading `err.message` unchanged. An unknown code falls back to the server's
 * own (Indonesian) message, which is always present. The locale is read at
 * throw time; an error already on screen does not restyle when the switch is
 * flipped, which is fine for something transient.
 */
function localizeError(message: string, code: string | undefined, params: Vars | undefined): string {
  if (!code) return message;
  const text = translate(readStoredLocale(), code, params);
  // translate() echoes the key back when it is unknown; prefer the server
  // message in that case rather than showing a raw code.
  return text === code ? message : text;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(
      0,
      translate(readStoredLocale(), 'err.network'),
      undefined,
      'err.network',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(
        response.status,
        translate(readStoredLocale(), 'err.invalidResponse'),
        undefined,
        'err.invalidResponse',
      );
    }
  }

  if (!response.ok) {
    const body = payload as
      | { error?: string; field?: string; code?: string; params?: Vars }
      | null;
    const fallback = body?.error ?? translate(readStoredLocale(), 'err.requestFailed', {
      status: response.status,
    });
    throw new ApiError(
      response.status,
      localizeError(fallback, body?.code, body?.params),
      body?.field,
      body?.code,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Formats whole rupiah as 'Rp 99.000'. */
export function formatIdr(amount: number): string {
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

export function formatDateId(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}
